import * as React from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import { useEffect } from 'react';
import { SplashScreen } from '@capacitor/splash-screen';
import { StreamChat } from 'stream-chat';
import { useQueryClient } from '@tanstack/react-query';
import Helmet from '../commonComponents/Helmet';
import AnonymousOnlyRoute from './AnonymousOnlyRoute';
import PublicJoinPage from './pages/public/PublicJoinPage';
import PublicClientTypeSelectionPage from './pages/public/PublicClientTypeSelectionPage';
import PublicBasicInformationPage from './pages/public/PublicBasicInformationPage';
import PublicFamilyMembersPage from './pages/public/PublicFamilyMembersPage';
import PublicBusinessEntitiesPage from './pages/public/PublicBusinessEntitiesPage';
import PublicInterestsPage from './pages/public/PublicInterestsPage';
import PublicProfileReviewPage from './pages/public/PublicProfileReviewPage';
import PublicProfileCompletionPage from './pages/public/PublicProfileCompletionPage';

// Version 2
import Loginv2 from './pages_v2/login/LoginPage';
import Registerv2 from './pages_v2/register/RegisterPage';
import ForgotPasswordV2 from './pages_v2/forgotPassword/ForgotPasswordPage';
import ResetPasswordV2 from './pages_v2/resetPassword/ResetPasswordPage';
import BbocV2 from './pages_v2/bboc/BbocPage';
import QuestionV2 from './pages_v2/bboc/questions/QuestionPage';
import ProfileV2 from './pages_v2/bboc/BbocProfilePage';

import {
	appHeaderHeight,
	bottomNavBarHeight,
	brandingColourHeavy,
	newContentPadding,
	popupZIndex,
	tabletPortraitMinWidth,
	whiteColour
} from './cssConstants';
import {
	bbocProfilePageUrl,
	bbocQuestionPageUrl,
	bbocQuestionsPageUrl,
	bbocResultPageUrl,
	bbocWelcomePageUrl,
	forgotPasswordPageUrl,
	loginPageUrl,
	resetPasswordPageUrl,
	signUpPageUrl,
	publicClientJoinPageUrl,
	publicClientTypeSelectionPageUrl,
	publicClientBasicInfoPageUrl,
	publicClientFamilyPageUrl,
	publicClientEntitiesPageUrl,
	publicClientInterestsPageUrl,
	publicClientReviewPageUrl,
	publicClientCompletionPageUrl,
	protectedFallbackPageUrl,
	helmetTitle,
	appPrivacyPolicyPageUrl
} from '../core/constants';
import config from '../config.general';
import { isLoggedIn } from '../core/localStorage';
import AppUrlListener from './common/AppUrlListener';
import { isWebApp } from '../lib/utils';
import { PublicClientProgressProvider } from './pages/public/common/PublicProgressContext';
import { getClientProfilePhotoUrl, isMcProgressApp } from '../core/domain';
import ProtectedFallbackPage from './pages_v2/ProtectedFallbackPage';
import AppPrivacyPolicyPage from './pages_v2/AppPrivacyPolicyPage';
import MobileOnlyRoute from './MobileOnlyRoute';
import useClientContextStore, { initializeClientStore } from '../zustandStore/clientContextStore';
import { useMessageStore } from '../zustandStore/messageStore';
import useChatStore from '../zustandStore/chatStore';
import useLoginStatusStore from '../zustandStore/loginStatusStore';
import useProfileCompletionStore, { initializeProfileCompletionStore } from '../zustandStore/profileCompletionStore';
import useNotificationStore from '../zustandStore/notificationStore';
import AppPublicDataPrefetch from './common/AppPublicDataPrefetch';
import BottomNavBarV2 from './pages_v2/dashboard/layouts/BottomNavBarV2';
import EventTourProvider from './pages_v2/common/guidedTour/EventTourProvider';

import PostLoginPromptHandler from './common/PostLoginPromptHandler';
import { useGetNotifications } from '../core/api/pushNotification';
import { getNewNotifications } from './pages_v2/notification/NotificationsCentrePage';
import { createDefaultNotificationChannel } from './common/pushNotification/notificationChannel';

const chatClient = new StreamChat(config.streamApiKey, { timeout: 10000 });

const App = () => {
	const loggedIn = useLoginStatusStore((state) => state.loggedIn);
	const fetchClientDetails = useClientContextStore((state) => state.fetchClientDetails);
	const fetchProfileCompletion = useProfileCompletionStore((state) => state.fetchProfileCompletion);
	const updateMessage = useMessageStore((state) => state.updateMessage);
	const clientDetails = useClientContextStore((state) => state.clientDetails);
	const setChatClient = useChatStore((state) => state.setChatClient);
	const disconnectChatClient = useChatStore((state) => state.disconnectChatClient);
	const setUnreadNotificationCount = useNotificationStore((state) => state.setUnreadNotificationCount);

	const queryClient = useQueryClient();
	const userNotifications = useGetNotifications(false); // Only fetch when logged in

	// Pass queryClient to the Zustand store
	initializeClientStore(queryClient);
	initializeProfileCompletionStore(queryClient);

	useEffect(() => {
		if (!isWebApp) {
			const hideSplashScreen = async () => {
				// Create default notification channel for Android
				await createDefaultNotificationChannel();
				await SplashScreen.hide({ fadeOutDuration: 500 });
			};
			hideSplashScreen();
		}
	}, []);

	useEffect(() => {
		if (loggedIn) {
			const getClientDetailsAndProfileCompletion = async () => {
				const [errorInFetchClientDetails, errorInFetchProfileCompletion] = await Promise.all([
					fetchClientDetails(), // Get initial client details context
					fetchProfileCompletion() // Get initial profile completion state
				]);
				if (errorInFetchClientDetails || errorInFetchProfileCompletion) {
					const error = [errorInFetchClientDetails, errorInFetchProfileCompletion].filter(Boolean).join(' ');
					updateMessage(error);
				}
			};
			getClientDetailsAndProfileCompletion();
		}
	}, [loggedIn]);

	useEffect(() => {
		if (userNotifications) {
			const unreadNotifications = getNewNotifications(userNotifications);
			setUnreadNotificationCount(unreadNotifications.length);
		}
	}, [userNotifications]);

	useEffect(() => {
		if (clientDetails?.userKey) {
			const clientName = clientDetails?.preferredName || `${clientDetails?.firstName} ${clientDetails?.lastName}`;
			const clientProfilePhotoUrl = getClientProfilePhotoUrl(clientDetails.profilePhotoName);
			setChatClient(chatClient, queryClient, clientDetails.userKey, clientName, clientProfilePhotoUrl);
		}
		return () => {
			disconnectChatClient(chatClient);
		};
	}, [clientDetails?.userKey]);

	return (
		<>
			<Helmet defaultTitle={helmetTitle} titleTemplate={`%s | ${helmetTitle}`} />
			<AppPublicDataPrefetch />
			{/* {!isIosApp && <HomeScreenBox />} */}
			{/* <StyledEngineProvider injectFirst> */}
			{/* MuiThemeProvider is Required by Material UI v5 migration */}
			<GlobalStyle />
			<PublicClientProgressProvider>
				<BrowserRouter>
					{isMcProgressApp() && <PostLoginPromptHandler />}
					<AppUrlListener />
					{clientDetails?.invitedToEvent && <EventTourProvider />}
					<Routes>
						{anonymousOnlyRoutes.map(({ path, element }) => (
							<Route key={path} path={path} element={<AnonymousOnlyRoute>{element}</AnonymousOnlyRoute>} />
						))}
						<Route
							path={forgotPasswordPageUrl}
							element={
								<MobileOnlyRoute>
									<ForgotPasswordV2 />
								</MobileOnlyRoute>
							}
						/>
						<Route path={resetPasswordPageUrl} element={<ResetPasswordV2 />} />
						<Route path={bbocWelcomePageUrl} element={<BbocV2 />} />
						<Route path={bbocProfilePageUrl} element={<ProfileV2 />} />
						<Route path={bbocQuestionPageUrl} element={<QuestionV2 />} />
						<Route path={bbocQuestionsPageUrl} element={<QuestionV2 />} />
						<Route path={bbocResultPageUrl} element={<BbocV2 />} />
						<Route path={appPrivacyPolicyPageUrl} element={<AppPrivacyPolicyPage />} />
						<Route path={protectedFallbackPageUrl} element={<ProtectedFallbackPage />} />
						<Route
							path="*"
							element={
								<React.Suspense fallback={null}>
									<LazyRoutes />
								</React.Suspense>
							}
						/>
					</Routes>
					{isLoggedIn() && window.location.pathname !== appPrivacyPolicyPageUrl && <BottomNavBarV2 />}
				</BrowserRouter>
			</PublicClientProgressProvider>
			{/* </StyledEngineProvider> */}
			{/* <ReactQueryDevtools initialIsOpen={false} /> */}
		</>
	);
};

export default App;

const LazyRoutes = React.lazy(() => import(/* webpackChunkName: "lazyRoutes" */ './LazyRoutes'));

import(/* webpackChunkName: "lazyRoutes", webpackPrefetch: true */ './LazyRoutes');

const anonymousOnlyRoutes = [
	{
		path: loginPageUrl,
		element: (
			<MobileOnlyRoute>
				<Loginv2 />
			</MobileOnlyRoute>
		)
	},
	{
		path: signUpPageUrl,
		element: (
			<MobileOnlyRoute>
				<Registerv2 />
			</MobileOnlyRoute>
		)
	},
	{ path: publicClientJoinPageUrl, element: <PublicJoinPage /> },
	{ path: publicClientTypeSelectionPageUrl, element: <PublicClientTypeSelectionPage /> },
	{ path: publicClientBasicInfoPageUrl, element: <PublicBasicInformationPage /> },
	{ path: publicClientFamilyPageUrl, element: <PublicFamilyMembersPage /> },
	{ path: publicClientEntitiesPageUrl, element: <PublicBusinessEntitiesPage /> },
	{ path: publicClientInterestsPageUrl, element: <PublicInterestsPage /> },
	{ path: publicClientReviewPageUrl, element: <PublicProfileReviewPage /> },
	{ path: publicClientCompletionPageUrl, element: <PublicProfileCompletionPage /> }
];

const GlobalStyle = createGlobalStyle`
	/* Define CSS variables for safe area insets with fallbacks */
	:root {
		--safe-area-inset-top: env(safe-area-inset-top, 0px);
		--safe-area-inset-bottom: env(safe-area-inset-bottom, 0px);
	}

	body {
		margin: 0;
		font-weight: 400;
		-webkit-user-select: none;
		-webkit-touch-callout: none;
	}

	// Fix for iOS Safari scrolling issue for form input
	// Reference: https://stackoverflow.com/questions/29001977
	html, body {
    -webkit-overflow-scrolling: touch !important;
    overflow: auto !important;
    height: 100%;
	}

	* {
		font-family: ${({ theme }) => theme.fontFamily} !important;
	}

	*,
	*::before,
	*::after {
		box-sizing: border-box;
	}

	* {
 	 -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
 	 scrollbar-width: none; /* Hide scrollbar in Firefox */

	 // Prevent text selection and image drag on iOS
	 -webkit-user-select: none;
	 -webkit-user-drag: none;
	 -webkit-touch-callout: none;
	 -webkit-tap-highlight-color: transparent; /* Remove tap highlight on iOS */
	}

	#app {
		position: relative;
		min-height: 100%;
		overflow: hidden;
		background-color: ${whiteColour};
	}

	p {
		margin: 0;
	}

	button {
		cursor: pointer;
	}

	// Allow text selection for input fields and text areas where users need to select text
	input, textarea, [contenteditable="true"] {
		-webkit-user-select: text !important;
		-webkit-touch-callout: default !important;
	}

	// Ensure text selection is disabled for all other elements on iOS
	div, span, p, h1, h2, h3, h4, h5, h6, label, a, button, img, svg {
		-webkit-user-select: none !important;
		-webkit-touch-callout: none !important;
	}

	// Specifically target common UI elements that might still allow selection
	[role="button"], [role="tab"], [role="menuitem"], .MuiButton-root, .k-button {
		-webkit-user-select: none !important;
		-webkit-touch-callout: none !important;
	}

	// iOS specific - prevent all text selection with maximum specificity
	body * {
		-webkit-user-select: none !important;
		-webkit-touch-callout: none !important;
	}

	// Re-enable selection only for input elements
	body input, body textarea, body [contenteditable="true"] {
		-webkit-user-select: text !important;
		-webkit-touch-callout: default !important;
	}

	.k-list-container .k-popup {
		background-color: #fff;
		border: 1px solid rgba(0, 0, 0, 0.08);
		box-sizing: border-box;
		box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.04);
		border-radius: 7px;
		font-weight: normal;
		font-size: 18px;
		color: black;
		overflow: hidden;
		max-height: 250px;

		.k-list-scroller {
			max-height: 500px !important;

			.k-item {
				height: 40px;
			}

			.k-item.k-state-focused {
				box-shadow: none;
			}

			.k-item.k-state-selected {
				background: rgba(0, 0, 0, 0.1);
				color: #000;
			}

			.k-item:hover {
				background-color: rgba(0, 0, 0, 0.2);
			}
		}
	}

	.MuiModal-root, [role=dialog] {
		z-index: ${popupZIndex} !important;
		@media (min-width: ${tabletPortraitMinWidth}) {
			display: flex;
			justify-content: center;
		}
	}

	.right-aligned .k-cell-inner > .k-link {
		justify-content: right;
	}

	.k-grid-footer td {
		font-weight: normal;
		width: 100%;
		overflow: visible;
		white-space: nowrap;
		text-align: right;
	}

	// For PDFViewer selection hand buttons
	.k-button-solid-base.k-selected {
		border-color: ${brandingColourHeavy};
		background-color: ${brandingColourHeavy};
	}

	.k-tooltip {
		background-color: #1f2937;
		border-radius: 8px;
		max-width: 85vw;

		@media (min-width: ${tabletPortraitMinWidth}) {
			max-width: 450px;
		}
	}

  .k-popup:has(.k-chart-tooltip) {
		background: transparent !important;
		box-shadow: none !important;
		padding: 0 !important;
		border: none !important;
	}

	.k-animation-container:has(.k-menu-popup) {
		z-index: ${popupZIndex} !important;
	}

	.menuItem {
		font-size: 16px !important;
		padding: 5px;
	}

	// Stream Chat:
	.str-chat-channel.messaging {
		height: calc(100vh - ${appHeaderHeight} - ${bottomNavBarHeight});

		margin-left: -${newContentPadding} !important;
		margin-right: -${newContentPadding} !important;
		margin-top: -${newContentPadding} !important;
		margin-bottom: -${newContentPadding} !important;
	}

	.str-chat__channel-search .str-chat__channel-search-bar .str-chat__channel-search-bar-button {
		display: none;
	}

	.str-chat__channel-search .str-chat__channel-search-bar .str-chat__channel-search-input--wrapper:focus-within {
		border-color: #008AEC;
	}

	.str-chat__channel-search .str-chat__channel-search-bar .str-chat__channel-search-input--wrapper:focus-within .str-chat__channel-search-input--icon svg path {
		fill: #008AEC;
	}

	@supports (-webkit-touch-callout: none) { // to fix Stream chat reaction buttons too small issue in iOS only
		.str-chat__message-actions-box-button {
			width: 34px !important;
			height: 34px !important;

			> svg {
				width: 20px !important;
				height: 7px !important;
			}
		}

		.str-chat__message-reactions-button {
			width: 34px !important;
			height: 34px !important;

			> svg {
				width: 22px !important;
				height: 22px !important;
			}
		}
	}

	.react-joyride__tooltip > div:nth-child(2) > div:first-child {
		display: none !important; // hide the empty div so that Back button is aligned to the left
	}
`;
